import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Player,
  ControlBar,
  BigPlayButton,
  LoadingSpinner,
  PlayToggle,
  VolumeMenuButton,
  FullscreenToggle,
  CurrentTimeDisplay,
  DurationDisplay,
  ProgressControl,
} from "video-react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderIcon from "@mui/icons-material/Folder";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import MovieIcon from "@mui/icons-material/Movie";
import PersonIcon from "@mui/icons-material/Person";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import axios from "../Axios";
import { fmt, pct, rangeLabel } from "../../pages/videos/scenes/sceneModel";
import { parseDuration, resultTags } from "./nlqModel";
import { Marked, Tag } from "./ResultGroups";

import "../../../node_modules/video-react/dist/video-react.css";
import "../../pages/videos/Queries.css"; // control bar below the picture, as on the project pages

const PIN_ICONS = { speech: ChatBubbleIcon, moment: MovieIcon, person: PersonIcon };
const UNNAMED = /^(Character\d+|other)$/i;
const USER_SCROLL_PAUSE = 3000; // ms the transcript stops following playback after the user scrolls it

// transcripts of the videos already shown: projectId -> promise of [{sentence_id, start_time, end_time, speaker, text}]
const transcripts = new Map();

export function loadTranscript(projectId) {
  if (!transcripts.has(projectId)) {
    const sentences = axios.get("/video_project/" + projectId + "/sentences");
    const characters = axios.get("/video_project/" + projectId + "/characters");
    const promise = Promise.all([sentences, characters]).then(([s, c]) => {
      const names = {};
      c.data.forEach((x) => (names[x.char_id] = UNNAMED.test(x.char_name || "") ? "" : x.char_name));
      return s.data.map((x) => ({ ...x, speaker: names[x.char_id] || "" }));
    });
    promise.catch(() => transcripts.delete(projectId));
    transcripts.set(projectId, promise);
  }
  return transcripts.get(projectId);
}

const TranscriptLine = React.memo(function TranscriptLine({ line, speaker, active, hit, terms, spans, onSeek }) {
  return (
    <button type="button" className="mp-line" data-on={active || undefined} data-hit={hit || undefined} onClick={() => onSeek(line.start_time)}>
      <span className="mp-line-time">{fmt(line.start_time)}</span>
      <span className="mp-line-speaker">{speaker}</span>
      <span className="mp-line-text">{hit ? <Marked text={line.text} terms={terms} spans={spans} /> : line.text}</span>
    </button>
  );
});

// time range of a piece of evidence; clicking it moves the player there
function SeekRange({ start, end, onSeek }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="mp-why-time" title={t("Go to {{time}}", { time: fmt(start) })} onClick={() => onSeek(start)}>
      {rangeLabel(start, end)}
    </button>
  );
}

function Evidence({ r, highlight, onSeek }) {
  const { t } = useTranslation();
  const hits = r.lines.filter((l) => l.hit);
  const lines = hits.length ? hits : r.events.length ? [] : r.lines.slice(0, 2);
  return (
    <div className="mp-why">
      <span className="mp-caption">{t("How it matched")}</span>
      {r.reason && <p className="mp-reason">{r.reason}</p>}
      {lines.slice(0, 3).map((l) => (
        <div key={"l" + l.sentence_id} className="mp-why-item">
          <div className="mp-why-head">
            <Tag kind="speech" />
            <SeekRange start={l.start} end={l.end} onSeek={onSeek} />
          </div>
          <div className="mp-why-text">
            {l.speaker && t("{{name}} said", { name: l.speaker }) + " "}“<Marked text={l.text} terms={highlight.speech} spans={l.matches} />”
          </div>
        </div>
      ))}
      {r.events.slice(0, 3).map((e, i) => (
        <div key={"e" + i} className="mp-why-item">
          <div className="mp-why-head">
            <Tag kind="moment" />
            <SeekRange start={e.start} end={e.end} onSeek={onSeek} />
          </div>
          <div className="mp-why-text">
            <i>
              <Marked text={e.text} terms={highlight.visual} />
            </i>
          </div>
        </div>
      ))}
      {r.on_screen.slice(0, 3).map((o, i) => (
        <div key={"p" + i} className="mp-why-item">
          <div className="mp-why-head">
            <Tag kind="person" />
            <SeekRange start={o.start} end={o.end} onSeek={onSeek} />
          </div>
          <div className="mp-why-text">{t("{{name}} is on screen", { name: o.name })}</div>
        </div>
      ))}
    </div>
  );
}

// The selected moment: its video, the other moments of the video on a timeline, how it matched, and the transcript
// following playback
export default function MomentPreview({ r, video, pins, index, total, hovered, onSelect, onStep, onHover, highlight, videoSrc }) {
  const { t } = useTranslation();
  const player = useRef(null);
  const playerBox = useRef(null);
  const box = useRef(null);
  const lineRefs = useRef({});
  const autoScrollAt = useRef(0);
  const userScrollAt = useRef(0);
  const [now, setNow] = useState({ time: r.start, paused: true, duration: 0 });
  const [transcript, setTranscript] = useState({ projectId: null, lines: [], loaded: false });
  const [bar, setBar] = useState(null); // {left, right} of the player's progress bar in the panel

  const projectId = r.project_id;
  // the player's duration when known, so that the pins line up with its progress bar
  const duration = now.duration || parseDuration(video.duration);

  // the match pins sit under the progress bar, which moves as the time displays widen (e.g. 1:02:10)
  useEffect(() => {
    const holder = playerBox.current?.querySelector(".video-react-progress-holder");
    if (!holder) return;
    const measure = () => {
      const p = playerBox.current.getBoundingClientRect(),
        b = holder.getBoundingClientRect();
      if (b.width > 0) setBar({ left: Math.round(b.left - p.left), right: Math.round(p.right - b.right) });
    };
    measure();
    if (!window.ResizeObserver) return;
    const observer = new ResizeObserver(measure);
    observer.observe(holder);
    observer.observe(playerBox.current);
    return () => observer.disconnect();
  }, [projectId]);

  useEffect(() => {
    let current = true;
    setTranscript({ projectId, lines: [], loaded: false });
    loadTranscript(projectId)
      .then((lines) => current && setTranscript({ projectId, lines, loaded: true }))
      .catch((error) => {
        console.log(error);
        if (current) setTranscript({ projectId, lines: [], loaded: true });
      });
    return () => {
      current = false;
    };
  }, [projectId]);

  // the player is created for each video (key), so subscribe again when the video changes
  useEffect(() => {
    if (!player.current) return;
    return player.current.subscribeToStateChange((s) =>
      setNow((prev) =>
        prev.time === s.currentTime && prev.paused === s.paused && prev.duration === s.duration
          ? prev
          : { time: s.currentTime, paused: s.paused, duration: Number.isFinite(s.duration) ? s.duration : 0 },
      ),
    );
  }, [projectId]);

  // go to the selected moment
  useEffect(() => {
    setNow((prev) => ({ ...prev, time: r.start }));
    userScrollAt.current = 0;
    player.current?.seek(r.start);
  }, [r.id, r.start]);

  // Milvus speaker names for the lines of this video's moments, they are more complete than the characters'
  const speakers = {};
  const hitIds = new Set();
  const spans = {}; // matched words of the lines found by an exact text search
  pins.forEach((p) =>
    p.lines.forEach((l) => {
      speakers[l.sentence_id] = l.speaker;
      if (l.hit) hitIds.add(l.sentence_id);
      if (l.matches) spans[l.sentence_id] = l.matches;
    }),
  );
  r.lines.forEach((l) => (speakers[l.sentence_id] = l.speaker));

  const lines = transcript.projectId === projectId ? transcript.lines : [];
  let active = -1;
  lines.forEach((l, i) => {
    if (l.start_time - 0.5 <= now.time) active = i;
  });

  // keep the active line in the middle of the transcript, unless the user scrolled it recently
  useLayoutEffect(() => {
    const el = lineRefs.current[active];
    if (!el || !box.current) return;
    if (!now.paused && Date.now() - userScrollAt.current < USER_SCROLL_PAUSE) return;
    const top = el.offsetTop - box.current.clientHeight / 2 + el.offsetHeight / 2;
    if (Math.abs(box.current.scrollTop - top) > 1) {
      autoScrollAt.current = Date.now();
      box.current.scrollTop = top;
    }
  }, [active, lines.length, r.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const seek = (time) => {
    player.current?.seek(time);
    setNow((prev) => ({ ...prev, time }));
  };

  const scene = r.scene;
  const sceneTitle = scene.index < 0 ? video.project_name : scene.title || t("Scene") + " " + (scene.index + 1);
  return (
    <div className="mp">
      <div className="mp-player" ref={playerBox}>
        <Player key={projectId} ref={player} autoPlay={false} preload="metadata" src={videoSrc(projectId)} startTime={r.start}>
          <LoadingSpinner />
          <BigPlayButton position="center" />
          <ControlBar autoHide={false} disableDefaultControls>
            <PlayToggle key="play-toggle" order={1} />
            <VolumeMenuButton key="volume-menu-button" order={4} vertical />
            <CurrentTimeDisplay key="current-time-display" order={5.1} />
            <DurationDisplay key="duration-display" order={7.3} />
            <ProgressControl key="progress-control" order={6} />
            <FullscreenToggle key="fullscreen-toggle" order={8} />
          </ControlBar>
        </Player>
      </div>
      {duration > 0 && (
        <div className="mp-pins" style={bar ? { "--bar-left": bar.left + "px", "--bar-right": bar.right + "px" } : undefined}>
          <span className="mp-caption mp-pins-label">{t("Matches:", { nsSeparator: false })}</span>
          <div className="mp-pins-track">
            {pins.map((p) => (
              <button
                key={p.id}
                type="button"
                className="mp-pin"
                style={{ left: pct(p.start, duration) }}
                aria-current={p.id === r.id || undefined}
                data-hover={p.id === hovered || undefined}
                title={fmt(p.start) + (p.label === "match" ? "" : " · " + t("related"))}
                aria-label={fmt(p.start)}
                onClick={() => onSelect(p.id)}
                onMouseEnter={() => onHover(p.id)}
                onMouseLeave={() => onHover(null)}
              >
                {resultTags(p).map((k) => {
                  const Icon = PIN_ICONS[k];
                  return <Icon key={k} fontSize="inherit" />;
                })}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="mp-head">
        <div className="mp-head-text">
          <div className="mp-title">{sceneTitle}</div>
          <div className="mp-meta">
            <FolderIcon fontSize="inherit" />
            {video.folder && <span>{video.folder}</span>}
            {video.folder && <span>·</span>}
            <span>{video.project_name}</span>
            {scene.index >= 0 && <span>·</span>}
            {scene.index >= 0 && (
              <span className="mp-nowrap">
                {t("Scene")} {scene.index + 1} · {rangeLabel(scene.start, scene.end)}
              </span>
            )}
            {/* the video's Scenes tab, at this moment (Back comes back to these results) */}
            <Link className="mp-open" to={"/project/" + projectId + "/scenes?t=" + Math.floor(r.start)}>
              {t("Open at {{time}}", { time: fmt(r.start) })}
              <OpenInNewIcon fontSize="inherit" />
            </Link>
          </div>
        </div>
        <div className="mp-nav">
          <button type="button" className="mp-nav-btn" title={t("Previous moment")} aria-label={t("Previous moment")} disabled={index <= 0} onClick={() => onStep(-1)}>
            <ChevronLeftIcon fontSize="inherit" />
          </button>
          <span>{t("Moment {{i}} of {{n}}", { i: index + 1, n: total })}</span>
          <button type="button" className="mp-nav-btn" title={t("Next moment")} aria-label={t("Next moment")} disabled={index >= total - 1} onClick={() => onStep(1)}>
            <ChevronRightIcon fontSize="inherit" />
          </button>
        </div>
      </div>
      <Evidence r={r} highlight={highlight} onSeek={seek} />
      <div className="mp-transcript">
        <span className="mp-caption">{t("Transcript")}</span>
        <div
          ref={box}
          className="mp-lines"
          onScroll={() => {
            if (Date.now() - autoScrollAt.current > 150) userScrollAt.current = Date.now();
          }}
        >
          {lines.map((l, i) => (
            <div key={l.sentence_id} ref={(el) => (lineRefs.current[i] = el)}>
              <TranscriptLine
                line={l}
                speaker={speakers[l.sentence_id] || l.speaker}
                active={i === active}
                hit={hitIds.has(l.sentence_id)}
                terms={highlight.speech}
                spans={spans[l.sentence_id]}
                onSeek={seek}
              />
            </div>
          ))}
          {!lines.length && <div className="mp-lines-empty">{transcript.loaded ? t("No transcript") : t("Loading…")}</div>}
        </div>
      </div>
    </div>
  );
}
